import type { SupplyReportLineDto } from '@eombor/contracts';

/*
 * HUJJAT SATRLARINING KO'RINISH MODELI.
 *
 * Butun sahifadagi eng qimmat xato shu yerda tug'ilgan edi: satr
 * joylashtirilgach u ro'yxatning boshiga KO'CHIRILARDI. Ko'chirish uchun
 * ro'yxat ikkiga bo'linib, ikkala bo'lak alohida saralanardi — natijada
 * hujjatdagi tartib buzilardi (3 → 1 → 2 → 4), aylantirish joyi
 * yo'qolardi va 233 satrli hisobotda odam o'z o'rnini topa olmasdi.
 *
 * Endi qoida bitta va u shu faylda qo'riqlanadi:
 *
 *   TARTIB — SERVERNIKI. Bu yerda `sort`, `reverse`, `splice` yoki
 *   guruhlash YO'Q va bo'lmaydi. Faqat `map` (holat qo'shish),
 *   `filter` (ko'rsatish) va `slice` (sahifalash) — uchalasi ham
 *   yangi massiv qaytaradi va elementlar ketma-ketligini saqlaydi.
 *
 * Satrning holati SAQLANMAYDI, hisoblanadi: yagona manba — savat
 * (`basket`), ya'ni serverga ketadigan yozuvlar. Ikkinchi nusxa
 * bo'lganda ular albatta bir-biridan uzoqlashadi.
 */

/**
 * SATRNING HOLATI — uchta, ikkita emas.
 *
 * `empty` ATAYLAB alohida. 1C hisobotida qoldig'i NOL bo'lgan satrlar
 * ham keladi (mahsulot katalogda bor, omborda yo'q) va ilgari ular
 * «Joylashtirildi» bo'lib chiqardi: qoida `remaining > 0` edi, nol
 * qoldiqdan esa joylashtiriladigani baribir nol chiqadi. Ekran
 * shunday satrni yashil qilib, hech kim tegmagan ishni BAJARILGAN deb
 * ko'rsatardi — sarlavhadagi `37 / 190` ham shuncha oshib ketardi.
 *
 * Endi «joylashtirildi» degani FAQAT bitta narsani anglatadi: shu
 * satrda joylashtirish kerak bo'lgan miqdor bor edi va u tugadi.
 */
export type ItemStatus = 'placed' | 'pending' | 'empty';

export interface SupplyItem extends SupplyReportLineDto {
  /**
   * `pending` — ish qolgan; `placed` — bor edi va tugadi;
   * `empty` — hisobotdagi qoldiq nol, joylashtiradigan narsaning o'zi yo'q.
   */
  status: ItemStatus;
  /** `toPlaceQty` dan savatdagi miqdor ayirilgani. Manfiy bo'lmaydi. */
  remaining: number;
}

export type ItemFilter = 'all' | 'placed' | 'pending' | 'empty';

export interface ItemCounts {
  all: number;
  placed: number;
  pending: number;
  /** Qoldig'i nol satrlar. Nol bo'lsa chip umuman chizilmaydi. */
  empty: number;
}

/**
 * Kasrli miqdorlar qo'shilganda `0.1 + 0.2` muammosi chiqadi va
 * «qolgani» hech qachon aniq nolga tushmasdi — satr esa shu sababli
 * abadiy `pending` bo'lib qolardi.
 */
const round3 = (value: number): number => Math.round(value * 1000) / 1000;

/**
 * Savatdagi miqdorlar satr bo'yicha yig'iladi.
 *
 * Bitta satr bir necha katakka bo'linishi mumkin (mayda mahsulot ikki
 * javonga sig'adi), shuning uchun bu «bormi?» emas, «qanchasi?» degan
 * savol.
 */
export const basketQtyByLine = (
  basket: readonly { lineId: string; qty: number }[],
): ReadonlyMap<string, number> => {
  const total = new Map<string, number>();
  for (const item of basket) total.set(item.lineId, (total.get(item.lineId) ?? 0) + item.qty);
  return total;
};

/**
 * Server satrlariga holat qo'shadi — TARTIBNI SAQLAB.
 *
 * `map` ataylab: u har element uchun bittadan yangi obyekt qaytaradi va
 * ketma-ketlikni o'zgartira olmaydi. Holat almashganda massivning
 * o'zi qayta tuzilmaydi, faqat bitta elementning maydoni boshqacha
 * bo'ladi — React esa `key` bo'yicha o'sha qatorni topib, unga yangi
 * sinf beradi. Shuning uchun DOM tugun O'RNIDA qoladi va aylantirish
 * joyi ham saqlanadi.
 */
export const buildItems = (
  lines: readonly SupplyReportLineDto[],
  inBasket: ReadonlyMap<string, number>,
): SupplyItem[] =>
  lines.map((line) => {
    const remaining = Math.max(0, round3(line.toPlaceQty - (inBasket.get(line.id) ?? 0)));
    /* Qoldiq AVVAL tekshiriladi: nol qoldiqda `remaining` ham baribir
       nol bo'ladi va tartib teskari bo'lsa satr yana `placed` bo'lib
       qolardi. Chegara serverdagi `hasWork` bilan bir xil. */
    const status: ItemStatus =
      line.balanceQty <= 0.0005 ? 'empty' : remaining > 0 ? 'pending' : 'placed';
    return { ...line, remaining, status };
  });

/** Kod ham, nom ham qidiriladi: skladchi kodni, buxgalter nomni izlaydi. */
export const matchesSearch = (item: SupplyItem, needle: string): boolean => {
  if (needle === '') return true;
  return (
    item.code.toLocaleLowerCase().includes(needle) ||
    item.name.toLocaleLowerCase().includes(needle)
  );
};

/**
 * Ko'rinadigan satrlar — QIDIRUV va CHIP birgalikda.
 *
 * Ikkalasi ham `filter`: ular ro'yxatni qisqartiradi, lekin qolganlarning
 * o'zaro tartibiga tegmaydi. Chip «joylashtirilganlarni tepaga chiqarish»
 * emas — u boshqa hamma narsani vaqtincha berkitish.
 */
export const visibleItems = (
  items: readonly SupplyItem[],
  search: string,
  filter: ItemFilter,
): SupplyItem[] => {
  const needle = search.trim().toLocaleLowerCase();
  return items.filter(
    (item) =>
      matchesSearch(item, needle) && (filter === 'all' || item.status === filter),
  );
};

/**
 * Chip yonidagi sonlar — QIDIRUVDAN oldin: chip nimani ochishini aytadi.
 *
 * Uchta son yig'indisi `all` ga teng bo'lishi SHART: odam chiplarni
 * qo'shib ko'radi va yig'indi jamiga to'g'ri kelmasa, qaysidir satr
 * yo'qolgandek tuyuladi.
 */
export const countItems = (items: readonly SupplyItem[]): ItemCounts => {
  let placed = 0;
  let empty = 0;
  for (const item of items) {
    if (item.status === 'placed') placed += 1;
    else if (item.status === 'empty') empty += 1;
  }
  return { all: items.length, placed, empty, pending: items.length - placed - empty };
};

/** Bitta sahifadagi satrlar soni — 233 qator DOM da bir vaqtda turmasin. */
export const PAGE_SIZE = 50;

/** `slice` — nusxa oladi, manbaga tegmaydi. */
export const pageItems = (items: readonly SupplyItem[], page: number): SupplyItem[] =>
  items.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE);

export const pageCount = (total: number): number =>
  total === 0 ? 1 : Math.ceil(total / PAGE_SIZE);
